import { BillingSubscription } from "../../../../types/subscription";

interface SubscriptionDetailsProps {
  open: boolean;
  onClose: () => void;
  subscription: BillingSubscription | null;
}

export default function SubscriptionDetails({
  open,
  onClose,
  subscription,
}: SubscriptionDetailsProps) {
  if (!open || !subscription) return null;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculateEndDate = (start: string, durationDays: number) => {
    const startDate = new Date(start);
    startDate.setDate(startDate.getDate() + durationDays);
    return formatDate(startDate.toISOString());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6">
        {/* Header */}
        <div className="flex items-center justify-between border-b pb-3">
          <h2 className="text-lg font-semibold">Subscription Details</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="grid grid-cols-2 gap-4 py-4 text-sm">
          <div>
            <p className="text-gray-500">User</p>
            <p className="font-medium">{subscription.username}</p>
          </div>

          <div>
            <p className="text-gray-500">Package</p>
            <p className="font-medium">{subscription.package_name}</p>
          </div>

          <div>
            <p className="text-gray-500">Price</p>
            <p className="font-medium">{subscription.price}</p>
          </div>

          <div>
            <p className="text-gray-500">Duration</p>
            <p className="font-medium">
              {subscription.duration} days
              {subscription.new_duration > subscription.duration &&
                ` → ${subscription.new_duration} days`}
            </p>
          </div>

          <div>
            <p className="text-gray-500">Payer ID</p>
            <p className="font-medium">{subscription.payerid}</p>
          </div>

          <div>
            <p className="text-gray-500">Service</p>
            <p className="font-medium">{subscription.service_name}</p>
          </div>

          <div>
            <p className="text-gray-500">Status</p>
            <span
              className={`px-2 py-1 rounded text-xs font-semibold ${
                subscription.status === "active"
                  ? "bg-green-100 text-green-700"
                  : subscription.status === "expired"
                  ? "bg-red-100 text-red-700"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {subscription.status}
            </span>
          </div>

          <div>
            <p className="text-gray-500">Created At</p>
            <p className="font-medium">{formatDate(subscription.created_at)}</p>
          </div>

          <div>
            <p className="text-gray-500">End Date</p>
            <p className="font-medium">
              {calculateEndDate(subscription.created_at, subscription.duration)}
            </p>
          </div>

          <div>
            <p className="text-gray-500">Renewed At</p>
            <p className="font-medium">
              {subscription.renewed_at
                ? formatDate(subscription.renewed_at)
                : "—"}
            </p>
          </div>

          <div>
            <p className="text-gray-500">Payment Ref</p>
            <p className="font-medium">{subscription.payment_reference}</p>
          </div>
        </div>

        {/* Content IDs */}
        {subscription.contentids?.length > 0 && (
          <div className="mt-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
            <p className="text-sm font-semibold text-gray-700 mb-1">
              Content IDs
            </p>
            <ul className="text-xs text-gray-600 space-y-1">
              {subscription.contentids.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end pt-4 border-t mt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
